import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { searchApi } from '~/api/search';
import authApi from '~/authApi';
import { CREATOR_CONTENT_QUERY_KEY } from '~/queries/keys';
import { FilterType } from '~/screens/PageBuilder/sidebar/ModalAddContent/types';

import { useGetAuthToken } from '../useGetAuthToken';

type UseFetchCreatorContent = {
  creatorId: string;
  creatorSlug?: string;
  isEnabled?: boolean;
  filters?: FilterType[];
  platform?: string;
  orderBy?: string;
  searchQuery?: string;
};

const getHeaders = (
  isSharedPage: boolean,
  creatorSlug?: string,
  authToken?: string | boolean,
) => {
  const headers = {} as Record<string, string>;

  if (isSharedPage) {
    headers['X-AUTH-TOKEN'] = `creator-share-${creatorSlug}`;
  } else if (!authToken && !creatorSlug && authApi.jwtAccessToken) {
    headers['X-AUTH-TOKEN'] = authApi.jwtAccessToken.accessToken;
  }

  return headers;
};

const getContentFilters = (creatorId: string, filters?: FilterType[]) => {
  const baseFilter = {
    comparator: 'eq',
    name: 'creator_id',
    value: creatorId,
  };

  return filters ? [baseFilter, ...filters] : [baseFilter];
};

export const useFetchCreatorContent = ({
  creatorId,
  creatorSlug,
  isEnabled = true,
  filters,
  platform,
  orderBy = 'published_at',
  searchQuery,
}: UseFetchCreatorContent) => {
  const router = useRouter();
  const isSharedPage = router.pathname.includes('/shared/');
  const authToken = useGetAuthToken({ creatorSlug });
  const headers = getHeaders(isSharedPage, creatorSlug, authToken);

  return useQuery(
    [
      CREATOR_CONTENT_QUERY_KEY,
      { creatorId, creatorSlug, platform, filters, orderBy, searchQuery },
    ],
    () => {
      return searchApi
        .post(
          '/contents',
          {
            filters: getContentFilters(creatorId, filters),
            platforms: platform,
            q: searchQuery,
            size: 32,
            order_by: orderBy,
            order: 'desc',
          },
          { headers },
        )
        .then((d) => d.data.results);
    },
    {
      enabled: isEnabled && Boolean(creatorId),
      staleTime: 0,
      retry: 0,
    },
  );
};

export const useFetchCreatorContentInfinite = ({
  creatorId,
  creatorSlug,
  isEnabled = true,
  filters,
  platform,
  orderBy = 'published_at',
  searchQuery,
}: UseFetchCreatorContent) => {
  const router = useRouter();
  const isSharedPage = router.pathname.includes('/shared/');
  const authToken = useGetAuthToken({ creatorSlug });
  const headers = getHeaders(isSharedPage, creatorSlug, authToken);

  return useInfiniteQuery({
    queryKey: [
      CREATOR_CONTENT_QUERY_KEY,
      { creatorId, creatorSlug, platform, filters, orderBy, searchQuery },
    ],
    queryFn: ({ pageParam = 1 }) => {
      return searchApi
        .post(
          '/contents',
          {
            filters: getContentFilters(creatorId, filters),
            platforms: platform,
            q: searchQuery,
            size: 32,
            order_by: orderBy,
            order: 'desc',
            page: pageParam,
          },
          { headers },
        )
        .then((d) => d.data.results);
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.length > 0 ? pages.length + 1 : undefined;
    },
    enabled: isEnabled && Boolean(creatorId),
    staleTime: 0,
    retry: 0,
  });
};
